import { Customer } from 'types';

export type DatePreset = 'custom' | 'today' | 'this_week' | 'this_month' | 'last_month' | 'this_quarter' | 'this_year' | 'last_year';

export type StatusFilter = 'all' | 'draft' | 'finalized' | 'paid' | 'unpaid';

export interface FilterState {
  customer: Customer | null;
  status: StatusFilter;
  dateFrom: string;
  dateTo: string;
  datePreset: DatePreset;
  deadlineFrom: string;
  deadlineTo: string;
  deadlinePreset: DatePreset;
}

export const statusFilterOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft (Not Finalized)' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

export const datePresetOptions: { value: DatePreset; label: string }[] = [
  { value: 'custom', label: 'Custom Range' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
];

export const initialFilters: FilterState = {
  customer: null,
  status: 'all',
  dateFrom: '',
  dateTo: '',
  datePreset: 'custom',
  deadlineFrom: '',
  deadlineTo: '',
  deadlinePreset: 'custom',
};

interface ApiFilter {
  field: string;
  operator: string;
  value: string | number | boolean;
}

const formatDateStr = (d: Date): string => d.toISOString().split('T')[0];

type DateRangeCalculator = (today: Date) => { from: string; to: string };

const datePresetCalculators: Record<DatePreset, DateRangeCalculator> = {
  custom: () => ({ from: '', to: '' }),
  today: (today) => ({ from: formatDateStr(today), to: formatDateStr(today) }),
  this_week: (today) => {
    // ISO week: Monday is first day of week
    // getDay() returns 0 for Sunday, 1 for Monday, etc.
    // We need to handle Sunday specially to avoid going forward to next week
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Days since Monday
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - diff);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return { from: formatDateStr(startOfWeek), to: formatDateStr(endOfWeek) };
  },
  this_month: (today) => {
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: formatDateStr(startOfMonth), to: formatDateStr(endOfMonth) };
  },
  last_month: (today) => {
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: formatDateStr(startOfLastMonth), to: formatDateStr(endOfLastMonth) };
  },
  this_quarter: (today) => {
    const quarter = Math.floor(today.getMonth() / 3);
    const startOfQuarter = new Date(today.getFullYear(), quarter * 3, 1);
    const endOfQuarter = new Date(today.getFullYear(), quarter * 3 + 3, 0);
    return { from: formatDateStr(startOfQuarter), to: formatDateStr(endOfQuarter) };
  },
  this_year: (today) => {
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    return { from: formatDateStr(startOfYear), to: formatDateStr(endOfYear) };
  },
  last_year: (today) => {
    const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
    const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
    return { from: formatDateStr(startOfLastYear), to: formatDateStr(endOfLastYear) };
  },
};

export const getDateRangeForPreset = (preset: DatePreset): { from: string; to: string } => {
  return datePresetCalculators[preset](new Date());
};

const statusFilterMap: Record<StatusFilter, ApiFilter | null> = {
  all: null,
  draft: { field: 'finalized', operator: 'eq', value: false },
  finalized: { field: 'finalized', operator: 'eq', value: true },
  paid: { field: 'paid', operator: 'eq', value: true },
  unpaid: { field: 'paid', operator: 'eq', value: false },
};

export const buildFilterParam = (filters: FilterState): string | undefined => {
  const apiFilters: ApiFilter[] = [];

  if (filters.customer) {
    apiFilters.push({ field: 'customer_id', operator: 'eq', value: filters.customer.id });
  }

  const statusFilter = statusFilterMap[filters.status];
  if (statusFilter) {
    apiFilters.push(statusFilter);
  }

  if (filters.dateFrom) {
    apiFilters.push({ field: 'date', operator: 'gteq', value: filters.dateFrom });
  }
  if (filters.dateTo) {
    apiFilters.push({ field: 'date', operator: 'lteq', value: filters.dateTo });
  }
  if (filters.deadlineFrom) {
    apiFilters.push({ field: 'deadline', operator: 'gteq', value: filters.deadlineFrom });
  }
  if (filters.deadlineTo) {
    apiFilters.push({ field: 'deadline', operator: 'lteq', value: filters.deadlineTo });
  }

  return apiFilters.length > 0 ? JSON.stringify(apiFilters) : undefined;
};

export const parseFiltersFromUrl = (searchParams: URLSearchParams): Partial<FilterState> => {
  const filters: Partial<FilterState> = {};
  
  const status = searchParams.get('status');
  if (status && ['all', 'draft', 'finalized', 'paid', 'unpaid'].includes(status)) {
    filters.status = status as StatusFilter;
  }
  
  const dateFrom = searchParams.get('dateFrom');
  if (dateFrom) filters.dateFrom = dateFrom;
  
  const dateTo = searchParams.get('dateTo');
  if (dateTo) filters.dateTo = dateTo;
  
  const deadlineFrom = searchParams.get('deadlineFrom');
  if (deadlineFrom) filters.deadlineFrom = deadlineFrom;
  
  const deadlineTo = searchParams.get('deadlineTo');
  if (deadlineTo) filters.deadlineTo = deadlineTo;
  
  return filters;
};

export const hasActiveFilters = (filters: FilterState): boolean => {
  return (
    filters.customer !== null ||
    filters.status !== 'all' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.deadlineFrom !== '' ||
    filters.deadlineTo !== ''
  );
};

export const countActiveFilters = (filters: FilterState): number => {
  let count = 0;
  if (filters.customer) count++;
  if (filters.status !== 'all') count++;
  if (filters.dateFrom || filters.dateTo) count++;
  if (filters.deadlineFrom || filters.deadlineTo) count++;
  return count;
};
